import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";

import { Button, Card, Popconfirm, Spin, Tabs, Tag, message } from "antd";

import { _deleteSupplierPriceList, _getAdmins, _getSupplierPriceList, _getSupplierPriceListUploads } from "../../services";
import { errorMessage } from "../term-contract/helpers";
import PriceListHeaderModal from "./header-modal";
import PriceListLines from "./lines";
import { ImportButton } from "./import";
import { TemplateActions, UploadsTable } from "./uploads";
import { uploadLabel } from "./helpers";

const MUTED = "rgba(0,0,0,0.45)";

/** One supplier's price list: header, Lines and Uploads tabs, import (the main path) and the template upload / download. */
class PriceListProfile extends React.Component {

    state = { priceList: null, uploads: [], loadingUploads: false, admins: {}, lineTotal: null, editing: false, activeTab: "lines" };

    lines = React.createRef();

    componentDidMount() {
        this.load();
        this.loadUploads();
        _getAdmins()
            .then(res => this.setState({
                admins: (res.data.users || []).reduce((names, admin) => ({
                    ...names,
                    [admin._id]: (admin.details && admin.details.name) || (admin.local && admin.local.email) || admin._id
                }), {})
            }))
            .catch(e => console.error("Failed to load admins", e));
    }

    load = async () => {
        try {
            const { data } = await _getSupplierPriceList({ id: this.props.priceListId });
            this.setState({ priceList: data.priceList });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    loadUploads = async () => {
        this.setState({ loadingUploads: true });
        try {
            const { data } = await _getSupplierPriceListUploads({ id: this.props.priceListId });
            this.setState({ uploads: data.uploads });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ loadingUploads: false });
        }
    };

    /* after an upload or import: new lines and a new upload row */
    afterUpload = () => {
        if (this.lines.current) this.lines.current.reload();
        this.loadUploads();
    };

    remove = async () => {
        try {
            await _deleteSupplierPriceList({ id: this.state.priceList._id });
            message.success("Price list deleted");
            this.props.history.push("/supplier/price-lists");
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    adminName = ({
        userId,
    }) => this.state.admins[userId] || userId || "-";

    /* the price history's "From" column */
    uploadLabel = ({
        uploadId,
    }) => {
        const upload = this.state.uploads.find(u => `${u._id}` === `${uploadId}`);
        if (!upload) return "An upload";
        return uploadLabel({ upload });
    };

    renderHeader({
        readOnly,
    }) {
        const { priceList } = this.state;
        return (
            <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12 }}><Link to="/supplier/price-lists">Supplier price lists</Link> / {priceList.sellerName}</div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 8, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                        <h2 style={{ margin: 0 }}>{priceList.sellerName} <Tag>{priceList.currency}</Tag>{readOnly && <Tag color="red">Deleted</Tag>}</h2>
                        <div style={{ color: MUTED }}>
                            IST seller {priceList.sellerId} · ETH supplier {priceList.ethSupplierId || "not set (no auto-linking)"}
                        </div>
                        {priceList.notes && <div style={{ marginTop: 4 }}>{priceList.notes}</div>}
                    </div>
                    {!readOnly && <Button icon="edit" onClick={() => this.setState({ editing: true })}>Edit details</Button>}
                    {!readOnly && (
                        <Popconfirm title="Delete this price list? It stops matching at once." onConfirm={this.remove}>
                            <Button type="danger" icon="delete">Delete</Button>
                        </Popconfirm>
                    )}
                </div>
            </Card>
        );
    }

    render() {
        const { priceList, uploads, loadingUploads, lineTotal, editing, activeTab } = this.state;
        if (!priceList) return <Card><Spin /></Card>;

        const readOnly = !!priceList.deletedAt;
        return (
            <div>
                {this.renderHeader({ readOnly })}
                <Card>
                    <Tabs
                        activeKey={activeTab}
                        onChange={key => this.setState({ activeTab: key })}
                        tabBarExtraContent={(
                            <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                                {!readOnly && activeTab === "lines" && <Button icon="plus" onClick={() => this.lines.current.add()}>Add line</Button>}
                                <TemplateActions priceList={priceList} readOnly={readOnly} onUploaded={this.afterUpload} />
                                {!readOnly && <ImportButton priceList={priceList} onImported={this.afterUpload} />}
                            </span>
                        )}
                    >
                        <Tabs.TabPane tab={`Lines${lineTotal === null ? "" : ` (${lineTotal.toLocaleString()})`}`} key="lines">
                            <PriceListLines
                                ref={this.lines}
                                priceList={priceList}
                                readOnly={readOnly}
                                uploadLabel={this.uploadLabel}
                                onTotal={({ total }) => this.setState({ lineTotal: total })}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={`Uploads (${uploads.length})`} key="uploads">
                            <UploadsTable priceList={priceList} uploads={uploads} loading={loadingUploads} adminName={this.adminName} />
                        </Tabs.TabPane>
                    </Tabs>
                </Card>
                {editing && (
                    <PriceListHeaderModal
                        priceList={priceList}
                        onCancel={() => this.setState({ editing: false })}
                        onSaved={updated => this.setState({ priceList: updated, editing: false })}
                    />
                )}
            </div>
        );
    }
}

export default withRouter(PriceListProfile);



// WEBPACK FOOTER //
// ./src/components/supplier-price-list/profile.js